import { useState, useEffect, useMemo, useRef } from "react";
import { FileText, Database, ChevronRight, ChevronDown, Lock, AlertTriangle } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { UNREVIEWED_GRID, type GridReviewMap, type GridReviewState } from "@/lib/extractionReview";
import type { Document } from "@/types/models";

export interface SectionSourceModalProps {
  open: boolean;
  onClose: () => void;
  sectionName: string;
  documents: Document[];
  extractionPoints: { id: string; name: string; parent_id?: string | null }[];
  extractionDocuments: { documentId: string; projectTemplateId: string }[];
  gridReview: GridReviewMap;
  isExtractionLoading?: boolean;
  hasExtractionError?: boolean;
  selectedDocumentIds: string[];
  selectedExtractionPointIds: string[];
  onSave: (docIds: string[], epIds: string[]) => void;
}

const DATA_POINT_MESSAGE_CLASS = "text-sm text-muted-foreground text-center py-8";

interface TemplateGroup {
  id: string;
  name: string;
  columns: { id: string; name: string }[];
  review: GridReviewState;
}

function buildTemplateGroups(
  extractionPoints: { id: string; name: string; parent_id?: string | null }[],
  gridReview: GridReviewMap
): TemplateGroup[] {
  const hasHierarchy = extractionPoints.some((ep) => ep.parent_id);

  if (!hasHierarchy) {
    return extractionPoints.map((ep) => ({
      id: ep.id,
      name: ep.name,
      columns: [],
      review: gridReview.get(ep.id) ?? UNREVIEWED_GRID,
    }));
  }

  return extractionPoints
    .filter((ep) => !ep.parent_id)
    .map((parent) => ({
      id: parent.id,
      name: parent.name,
      columns: extractionPoints
        .filter((ep) => ep.parent_id === parent.id)
        .map((ep) => ({ id: ep.id, name: ep.name })),
      review: gridReview.get(parent.id) ?? UNREVIEWED_GRID,
    }));
}

/** `null` hides the grid: not reviewed, and nothing saved from it to remove. */
function templateRowState(
  template: TemplateGroup,
  selectedIds: string[]
): "available" | "removable" | "empty" | null {
  if (template.columns.length === 0) return "empty";
  if (template.review.isReviewed) return "available";
  return template.columns.some((col) => selectedIds.includes(col.id)) ? "removable" : null;
}

interface TemplateGroupRowProps {
  template: TemplateGroup;
  state: "available" | "removable" | "empty";
  selectedIds: string[];
  isCollapsed: boolean;
  onToggleCollapse: () => void;
  onToggleAll: () => void;
  onToggleColumn: (colId: string) => void;
}

const TemplateGroupRow = ({
  template,
  state,
  selectedIds,
  isCollapsed,
  onToggleCollapse,
  onToggleAll,
  onToggleColumn,
}: TemplateGroupRowProps) => {
  const isEmpty = state === "empty";
  const isRemovable = state === "removable";
  const anySelected = template.columns.some((col) => selectedIds.includes(col.id));
  const pendingNoteId = `${template.id}-pending`;

  // A pending grid offers only what was already saved, so a stale choice can be cleared but not extended.
  const visibleColumns = isRemovable
    ? template.columns.filter((col) => selectedIds.includes(col.id))
    : template.columns;

  return (
    <li>
      <div
        className={`flex items-center gap-3 py-3 px-1 select-none ${isEmpty ? "opacity-60" : "cursor-pointer hover:bg-muted/40"}`}
        onClick={() => { if (!isEmpty) onToggleCollapse(); }}
      >
        <Checkbox
          checked={anySelected}
          disabled={isEmpty}
          aria-describedby={isRemovable ? pendingNoteId : undefined}
          onCheckedChange={onToggleAll}
          onClick={(e) => e.stopPropagation()}
          className="shrink-0"
        />
        <span className="flex-1 text-sm font-semibold text-foreground">{template.name}</span>
        {isEmpty && (
          <span className="shrink-0 text-xs text-muted-foreground">No data points configured</span>
        )}
        {isRemovable && (
          <span id={pendingNoteId} className="shrink-0 text-xs text-amber-800">
            Pending Quality Assurance — remove only
          </span>
        )}
        {!isEmpty && (
          <span className="shrink-0 text-muted-foreground">
            {isCollapsed
              ? <ChevronRight className="w-4 h-4" />
              : <ChevronDown className="w-4 h-4" />}
          </span>
        )}
      </div>

      {!isEmpty && !isCollapsed && (
        <ul>
          {visibleColumns.map((col) => (
            <li
              key={col.id}
              className="flex items-center gap-3 py-2.5 pl-10 pr-3 cursor-pointer hover:bg-muted/30"
              onClick={() => onToggleColumn(col.id)}
            >
              <Checkbox
                checked={selectedIds.includes(col.id)}
                onCheckedChange={() => onToggleColumn(col.id)}
                onClick={(e) => e.stopPropagation()}
                className="shrink-0"
              />
              <p className="text-sm text-foreground leading-snug">{col.name}</p>
            </li>
          ))}
        </ul>
      )}
    </li>
  );
};

const SectionSourceModal = ({
  open,
  onClose,
  sectionName: _sectionName,
  documents,
  extractionPoints,
  extractionDocuments,
  gridReview,
  isExtractionLoading,
  hasExtractionError,
  selectedDocumentIds,
  selectedExtractionPointIds,
  onSave,
}: SectionSourceModalProps) => {
  const [search, setSearch] = useState("");
  const [localDocIds, setLocalDocIds] = useState<string[]>([]);
  const [localEpIds, setLocalEpIds] = useState<string[]>([]);
  const [collapsedTemplates, setCollapsedTemplates] = useState<Set<string>>(new Set());
  const [studiesCollapsed, setStudiesCollapsed] = useState(true);

  const templates = useMemo(
    () => buildTemplateGroups(extractionPoints, gridReview),
    [extractionPoints, gridReview]
  );

  const hasRestored = useRef(false);

  // Restore once per opening: an upstream refetch rebuilds gridReview and so templates,
  // and re-running on that churn would discard the user's unsaved selections.
  useEffect(() => {
    if (!open) {
      hasRestored.current = false;
      return;
    }
    if (hasRestored.current) return;

    // Saved ids are validated against these lists; restoring before they arrive prunes them all.
    if (documents.length === 0 && selectedDocumentIds.length > 0) return;
    if (templates.length === 0 && selectedExtractionPointIds.length > 0) return;

    hasRestored.current = true;
    setSearch("");
    setCollapsedTemplates(new Set());
    setStudiesCollapsed(false);
    setLocalDocIds(selectedDocumentIds.filter((id) => documents.some((d) => d.id === id)));

    // Restore saved column IDs (not template IDs)
    const validColumnIds = new Set(templates.flatMap((t) => t.columns.map((c) => c.id)));
    setLocalEpIds(selectedExtractionPointIds.filter((id) => validColumnIds.has(id)));
  }, [open, selectedDocumentIds, selectedExtractionPointIds, documents, templates]);

  const q = search.trim().toLowerCase();
  const filteredDocs = q
    ? documents.filter(
        (d) => d.title.toLowerCase().includes(q) || d.author.toLowerCase().includes(q)
      )
    : documents;

  // Derive which documents are auto-included based on selected data point columns
  const selectedTemplateIds = new Set(
    extractionPoints
      .filter((ep) => localEpIds.includes(ep.id) && ep.parent_id != null)
      .map((ep) => ep.parent_id!)
  );
  const includedDocIds = [
    ...new Set(
      extractionDocuments
        .filter((td) => selectedTemplateIds.has(td.projectTemplateId))
        .map((td) => td.documentId)
    ),
  ];
  const includedDocs = includedDocIds
    .map((id) => documents.find((d) => d.id === id))
    .filter(Boolean) as Document[];

  const toggleDoc = (id: string) => {
    setLocalDocIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  // Template click: if any column selected → deselect all; otherwise → select all
  const toggleTemplateColumns = (colIds: string[]) => {
    const anySelected = colIds.some((id) => localEpIds.includes(id));
    if (anySelected) {
      setLocalEpIds((prev) => prev.filter((id) => !colIds.includes(id)));
    } else {
      setLocalEpIds((prev) => {
        const s = new Set(prev);
        colIds.forEach((id) => s.add(id));
        return [...s];
      });
    }
  };

  // Column click: toggle just that column
  const toggleColumn = (colId: string) => {
    setLocalEpIds((prev) =>
      prev.includes(colId) ? prev.filter((id) => id !== colId) : [...prev, colId]
    );
  };

  const toggleCollapse = (id: string) => {
    setCollapsedTemplates((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const handleDone = () => {
    onSave(localDocIds, localEpIds);
    onClose();
  };

  const visibleTemplates = templates.flatMap((template) => {
    const state = templateRowState(template, localEpIds);
    return state === null ? [] : [{ template, state }];
  });

  const renderDataPoints = () => {
    if (isExtractionLoading) {
      return <p className={DATA_POINT_MESSAGE_CLASS}>Loading the data extraction grid…</p>;
    }
    if (hasExtractionError) {
      return (
        <p className="text-sm text-destructive text-center py-8">
          The data extraction grid could not be loaded. Try again.
        </p>
      );
    }
    if (templates.length === 0) {
      return (
        <p className={DATA_POINT_MESSAGE_CLASS}>
          No extraction template is attached to this project.
        </p>
      );
    }
    if (visibleTemplates.length === 0) {
      return (
        <p className={DATA_POINT_MESSAGE_CLASS}>
          These will be available after you have completed the 'Quality Assurance' Step.
        </p>
      );
    }
    return (
      <ul>
        {visibleTemplates.map(({ template, state }) => {
          const columnIds = template.columns.map((col) => col.id);
          return (
            <TemplateGroupRow
              key={template.id}
              template={template}
              state={state}
              selectedIds={localEpIds}
              isCollapsed={collapsedTemplates.has(template.id)}
              onToggleCollapse={() => toggleCollapse(template.id)}
              onToggleAll={() => toggleTemplateColumns(columnIds)}
              onToggleColumn={toggleColumn}
            />
          );
        })}
      </ul>
    );
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <DialogContent className="max-w-lg flex flex-col max-h-[80vh]">
        <DialogHeader className="shrink-0">
          <DialogTitle>Add Source to Section</DialogTitle>
          <DialogDescription>
            Choose how this section will be sourced — from selected publications, or from the structured data extracted in your extraction grid.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="publications" className="flex flex-col min-h-0 flex-1">
          <TabsList className="shrink-0 w-full">
            <TabsTrigger value="publications" className="flex-1 gap-1.5 data-[state=active]:bg-brand-teal data-[state=active]:text-white">
              <FileText className="w-4 h-4" />
              Publications
            </TabsTrigger>
            <TabsTrigger value="extraction-points" className="flex-1 gap-1.5 data-[state=active]:bg-brand-teal data-[state=active]:text-white">
              <Database className="w-4 h-4" />
              Extracted Data
            </TabsTrigger>
          </TabsList>

          <TabsContent value="publications" className="flex flex-col min-h-0 flex-1 mt-0">
            <p className="shrink-0 my-4 text-xs text-muted-foreground">Select individual publications from the source library to cite in this section.</p>

            <input
              type="text"
              placeholder="Search publications..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="shrink-0 w-full border border-input rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring mb-2"
            />

            <div className="flex-1 overflow-y-auto min-h-0">
              {filteredDocs.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-8">
                  {q ? "No publications match your search." : "No publications available for this project."}
                </p>
              ) : (
                <ul className="divide-y">
                  {filteredDocs.map((doc) => (
                    <li
                      key={doc.id}
                      className="flex items-start gap-3 py-3 px-1 cursor-pointer"
                      onClick={() => toggleDoc(doc.id)}
                    >
                      <Checkbox
                        checked={localDocIds.includes(doc.id)}
                        onCheckedChange={() => toggleDoc(doc.id)}
                        onClick={(e) => e.stopPropagation()}
                        className="mt-0.5 shrink-0"
                      />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-foreground leading-snug">{doc.title}</p>
                        <p className="text-xs text-muted-foreground mt-0.5">
                          {[doc.author, doc.year].filter(Boolean).join(" • ")}
                        </p>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <p className="shrink-0 pt-2 text-xs text-muted-foreground text-right">
              {localDocIds.length} publication{localDocIds.length !== 1 ? "s" : ""} selected
            </p>
          </TabsContent>

          <TabsContent value="extraction-points" className="flex flex-col min-h-0 flex-1 mt-0">
            <div className="flex-1 overflow-y-auto min-h-0">
              {/* Included Studies — collapsible, read-only */}
              <div className="border-b">
                <button
                  className="flex w-full items-center gap-2 py-3 px-1 text-sm font-semibold select-none hover:bg-muted/40"
                  onClick={() => setStudiesCollapsed((v) => !v)}
                >
                  {studiesCollapsed
                    ? <ChevronRight className="w-4 h-4" />
                    : <ChevronDown className="w-4 h-4" />}
                  Included Studies ({includedDocs.length})
                </button>

                {!studiesCollapsed && (
                  <div className="pb-3 px-1 flex flex-col gap-2">
                    <Alert variant="warning">
                      <AlertTriangle className="w-4 h-4" />
                      <AlertTitle><span className="text-xs text-amber-700">Studies are pre-linked from your Data Extraction grid</span></AlertTitle>
                      <AlertDescription>
                        <span className="text-xs text-amber-700">The studies below cannot be changed here. To modify them, update your extraction grid.</span>
                      </AlertDescription>
                    </Alert>

                    {includedDocs.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-2">
                        No studies included — select data points below.
                      </p>
                    ) : (
                      <ul className="space-y-2">
                        {includedDocs.map((doc) => (
                          <li key={doc.id} className="flex items-start gap-3 py-2.5 px-3 border rounded-md">
                            <Lock className="w-4 h-4 mt-0.5 shrink-0 text-muted-foreground" />
                            <div className="min-w-0">
                              <p className="text-xs font-medium text-foreground leading-snug">{doc.title}</p>
                              <p className="text-xs text-muted-foreground mt-0.5">
                                {[doc.author, doc.year].filter(Boolean).join(" · ")}
                              </p>
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
              </div>

              {/* Select Data Points */}
              <p className="mt-3 mb-1 px-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Select Data Points
              </p>

              {renderDataPoints()}
            </div>

            <p className="shrink-0 pt-2 text-xs text-muted-foreground text-right">
              {localEpIds.length} data point{localEpIds.length !== 1 ? "s" : ""} selected
            </p>
          </TabsContent>
        </Tabs>

        <div className="shrink-0 flex justify-end gap-2 pt-2 border-t">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button variant="brand" onClick={handleDone}>Save Selection</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default SectionSourceModal;
